import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { AuthUserContext } from "../../context/AuthUserContext";
import { useNavigation } from "../../utils/navigation";
import { useTranslation } from "react-i18next";

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding-bottom: var(--spacing-md);
`;

const Title = styled.h2`
  color: var(--text-100);
  padding: 0;
`;

const AddButton = styled.button`
  border-radius: var(--border-radius-large);
  background-color: var(--primary-200);
  border: none;
  &:hover {
    background-color: var(--primary-100);
  }
`;

const GridContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled.figure`
  position: relative;
  background-color: var(--bg-100);
  width: 100%;
  margin: 0;
  overflow: hidden;
  box-shadow: var(--shadow-large);
  border-radius: var(--border-radius-large);
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xs);
  background-color: var(--primary-300);
  color: var(--primary-100);
  padding: var(--spacing-sm) var(--spacing-lg);
  font-size: var(--font-size-small);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const PrimaryDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--primary-200);
  display: inline-block;
`;

const CardBody = styled.div`
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs);
`;

const CardActions = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  padding: 0 var(--spacing-lg) var(--spacing-lg);
`;

const ActionButton = styled.button`
  border-radius: var(--border-radius-large);
  padding: var(--spacing-xxs) var(--spacing-lg);
  background-color: var(--bg-200);
  color: var(--text-100);
  border: none;
  &:hover {
    background-color: var(--bg-300);
  }
`;

const DeleteButton = styled(ActionButton)`
  background-color: transparent;
  color: var(--error-color);
  &:hover {
    background-color: var(--error-color);
    color: var(--bg-100);
  }
`;

const WarningContainer = styled.div`
  background-color: var(--primary-300);
  color: var(--primary-100);
  padding: var(--spacing-md) var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-small);
  border-radius: var(--border-radius-large);
`;

const ConfirmContainer = styled.div`
  background-color: #00000031;
  position: fixed;
  left: 0;
  top: 0;
  z-index: var(--zindex-modal);
  width: 100vw;
  height: 100vh;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ConfirmWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  background-color: var(--bg-100);
  padding: var(--spacing-xxl) var(--spacing-lg);
  width: 94%;
  border-radius: var(--border-radius-large);
  box-shadow: var(--shadow-large);
  text-align: center;
  @media (min-width: 768px) {
    width: 500px;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-xl);
`;

const ButtonTransparent = styled.button`
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius-large);
  cursor: pointer;
  color: var(--primary-200);
  background-color: var(--bg-200);
  border: none;
  &:hover {
    color: var(--bg-100);
    background-color: var(--primary-200);
  }
`;
const ButtonConfirm = styled(ButtonTransparent)`
  color: var(--bg-100);
  background-color: var(--error-color);
  &:hover {
    opacity: 0.85;
    background-color: var(--error-color);
  }
`;

const AddressCards = () => {
  const { t } = useTranslation();
  const { userProfile } = useContext(AuthUserContext);
  const navigate = useNavigate();
  const { goToAddAddressBook, goToEditAddressBook, deleteAddressBook } =
    useNavigation();
  const [deleteAddress, setDeleteAddress] = useState(false);

  const handleDelete = async (e, addressId) => {
    e.preventDefault();
    try {
      await deleteAddressBook(addressId);
    } catch (error) {
      console.error("Greška prilikom brisanja adrese:", error);
    }
    setDeleteAddress(false);
  };

  return (
    <>
      <Header>
        <Title>{t("ADDRESS_BOOK.TITLE")}</Title>
        <AddButton onClick={() => navigate(goToAddAddressBook())}>
          {t("ADDRESS_BOOK.ADD_NEW_ADDRESS")}
        </AddButton>
      </Header>

      {userProfile?.addresses.length === 0 ? (
        <WarningContainer>
          ⚠️ {t("ADDRESS_BOOK.NO_ADDRESSES")}
        </WarningContainer>
      ) : (
        <GridContainer>
          {userProfile?.addresses.map((address, index) => (
            <Card key={index}>
              <CardHead>
                {t("ADDRESS_BOOK.SHIPPING_ADDRESS")}
                {address.is_primary && <PrimaryDot aria-hidden="true" />}
              </CardHead>
              <CardBody>
                <p>
                  {userProfile.first_name} {userProfile.last_name} -{" "}
                  {address.type}
                </p>
                <p>
                  {address.country}, {address.city}, {address.postal_code}
                </p>
                <p>
                  {address.street} {address.street_number}
                </p>
                <p>Tel: {address.phone_number}</p>
              </CardBody>
              <CardActions>
                <ActionButton
                  onClick={() => navigate(goToEditAddressBook(address.id))}
                >
                  {t("BUTTONS.EDIT")}
                </ActionButton>
                {userProfile?.addresses.length !== 1 &&
                  !address.is_primary && (
                    <DeleteButton
                      onClick={() => setDeleteAddress(address)}
                    >
                      {t("BUTTONS.DELETE")}
                    </DeleteButton>
                  )}
              </CardActions>
            </Card>
          ))}
        </GridContainer>
      )}
      {deleteAddress && (
        <ConfirmContainer>
          <ConfirmWrapper>
            {t("ADDRESS_BOOK.CONFIRM_DELETE")}
            <ButtonContainer>
              <ButtonConfirm onClick={(e) => handleDelete(e, deleteAddress.id)}>
                {t("BUTTONS.CONFIRM")}
              </ButtonConfirm>
              <ButtonTransparent onClick={() => setDeleteAddress(false)}>
                {t("BUTTONS.CANCEL")}
              </ButtonTransparent>
            </ButtonContainer>
          </ConfirmWrapper>
        </ConfirmContainer>
      )}
    </>
  );
};

export default AddressCards;
